import clsx from "clsx";
import type { Feature, FeatureCollection } from "geojson";
import { type ComponentType, useMemo } from "react";

import { type Draft, useUi } from "../../state/ui";
import { revealPaint, revealStyle, useMapRevealed } from "../reveal";
import { useShownDeployment } from "../manoeuvre/useOrientation";
import { circle, hazardFan, type LonLat, translated } from "./geometry";
import { stackGraphicTags } from "./tagStack";

/** Graphiques tactiques sur la carte 2D (l'iPad ne les montrait qu'en bac à sable RA) : contour à la couleur
 *  réglementaire du camp (APP-6 : bleu ami, jaune NRBC liseré de noir), en trait plein, sur une surface
 *  légèrement teintée qui se lit sur un fond chargé ; lignes à la largeur relative des rubans de l'iPad (l'axe
 *  d'effort le plus large) ; chevron de l'axe d'effort. Étiquettes en puces de l'interface (charte web), comme
 *  les LIMA, sans recouvrement entre elles ni sur les unités (`stackGraphicTags`, au zoom réel). Couches sous les unités ;
 *  le graphique sélectionné est cerné de blanc. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
  /** Zoom et cap de la carte : étiquettes sans recouvrement. */
  zoom?: number;
  bearing?: number;
}

/** Couche de préhension des contours et des lignes (invisible, large) et surface des zones : sélection d'un clic,
 *  glissement du graphique sélectionné. */
export const GRAPHIC_EDGE_LAYER_ID = "graphics-hit-edge";
export const GRAPHIC_FILL_LAYER_ID = "graphics-fill";
export const GRAPHIC_LAYER_IDS = [GRAPHIC_EDGE_LAYER_ID, GRAPHIC_FILL_LAYER_ID];

const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
const AREA_ROLES = ["literal", ["zone", "plume-r", "plume-fan"]];
const STROKE_ROLES = ["literal", ["zone", "plume-r", "plume-fan", "line", "arrow"]];

// Rubans de l'iPad de 35 m (ligne de phase) à 90 m (axe d'effort), ramenés de 2,5 à 4 px ; contours 2,5 px.
const STROKE_WIDTH = ["case", ["has", "width_m"], ["interpolate", ["linear"], ["get", "width_m"], 35, 2.5, 90, 4], 2.5];

interface Tag {
  id: string;
  label: string;
  color: string;
  at: LonLat;
}

/** Étiquette d'un graphique : puce de l'interface, pastille de la couleur du type ; transparente aux gestes (le
 *  graphique se saisit par sa surface ou son contour). */
function GraphicTag({ label, color, selected }: { label: string; color: string; selected: boolean }) {
  return (
    <div
      className={clsx(
        // Police de l'interface imposée : le conteneur du moteur de carte impose Helvetica.
        "pointer-events-none flex h-5 items-center gap-1.5 rounded-md border bg-surface px-1.5 font-sans whitespace-nowrap shadow-[0_1px_2px_rgb(16_18_24/0.12)]",
        selected ? "border-accent ring-2 ring-accent/25" : "border-line-strong",
      )}
    >
      <span className="size-2 shrink-0 rounded-[2px] ring-1 ring-black/25" style={{ background: color }} />
      <span className="text-[12px] leading-none font-semibold text-ink">{label}</span>
    </div>
  );
}

export function GraphicsLayers({ Source, Layer, Marker, zoom = 10, bearing = 0 }: EngineParts) {
  const { data } = useShownDeployment(); // aperçu du sens de la manœuvre (docs/14 §2.14)
  const visible = useUi((s) => s.controlVisible.graphics !== false);
  const selection = useUi((s) => s.selection);
  const shift = useUi((s) => s.graphicShift);
  const revealed = useMapRevealed();
  const selectedId = selection?.layer === "graphic" ? selection.id : "";

  const collection = useMemo(() => {
    const base = data?.graphics_features ?? EMPTY;
    // Glissement en cours : le graphique suit le pointeur avant l'enregistrement (le serveur recalcule au relâcher).
    return shift ? translated(base, shift.id, shift.dLon, shift.dLat) : base;
  }, [data?.graphics_features, shift]);

  const tags = useMemo(
    () =>
      collection.features
        .filter((f) => f.properties?.role === "label" && f.geometry.type === "Point")
        .map(
          (f): Tag => ({
            id: String(f.properties?.id),
            label: String(f.properties?.label ?? ""),
            color: String(f.properties?.color ?? "#00A8DC"),
            at: (f.geometry as unknown as { coordinates: LonLat }).coordinates,
          }),
        ),
    [collection],
  );

  // Symboles des unités du temps affiché : les étiquettes s'en écartent (vers le haut ou le bas, au plus court).
  const phase = useUi((s) => s.phase);
  const unitsVisible = useUi((s) => s.unitsVisible);
  const units = data?.units;
  const unitPoints = useMemo<LonLat[]>(
    () =>
      (units ?? []).flatMap((unit) => {
        const pos = unit.positions[phase];
        return pos && !unit.hidden && unitsVisible[unit.side] ? [[pos.lon, pos.lat] as LonLat] : [];
      }),
    [units, phase, unitsVisible],
  );
  // Au zoom réel (au centième) : décalages exacts à l'écran, sans marge perdue entre deux quarts de niveau.
  const atZoom = Math.round(zoom * 100) / 100;
  const offsets = useMemo(() => stackGraphicTags(tags, atZoom, bearing, unitPoints), [tags, atZoom, bearing, unitPoints]);

  const visibility = visible ? "visible" : "none";
  const isSelected = ["==", ["get", "id"], selectedId];

  return (
    <>
      <Source id="gx-graphics" type="geojson" data={collection}>
        <Layer
          id={GRAPHIC_FILL_LAYER_ID}
          type="fill"
          filter={["in", ["get", "role"], AREA_ROLES]}
          layout={{ visibility }}
          paint={revealPaint(
            "fill",
            {
              "fill-color": ["get", "color"],
              "fill-opacity": [
                "+",
                ["match", ["get", "role"], "plume-r", 0.16, "plume-fan", 0.1, 0.08],
                ["case", isSelected, 0.08, 0],
              ],
            },
            revealed,
          )}
        />
        <Layer
          id="graphics-halo"
          type="line"
          filter={["all", ["in", ["get", "role"], STROKE_ROLES], isSelected]}
          layout={{ visibility, "line-cap": "round", "line-join": "round" }}
          paint={revealPaint(
            "line",
            { "line-color": "#ffffff", "line-width": ["+", STROKE_WIDTH, 5], "line-opacity": 0.95 },
            revealed,
          )}
        />
        {/* Liseré sombre sous le jaune NRBC, pour qu'il se lise sur fond clair. */}
        <Layer
          id="graphics-casing"
          type="line"
          filter={["all", ["in", ["get", "role"], STROKE_ROLES], ["==", ["get", "family"], "cbrnPlumes"]]}
          layout={{ visibility, "line-cap": "round", "line-join": "round" }}
          paint={revealPaint(
            "line",
            { "line-color": "#000000", "line-width": ["+", STROKE_WIDTH, 2], "line-opacity": 0.45 },
            revealed,
          )}
        />
        <Layer
          id="graphics-stroke"
          type="line"
          filter={["all", ["in", ["get", "role"], STROKE_ROLES], ["!=", ["get", "dashed"], true]]}
          layout={{ visibility, "line-cap": "round", "line-join": "round" }}
          paint={revealPaint("line", { "line-color": ["get", "color"], "line-width": STROKE_WIDTH }, revealed)}
        />
        <Layer
          id="graphics-stroke-dashed"
          type="line"
          filter={["all", ["in", ["get", "role"], STROKE_ROLES], ["==", ["get", "dashed"], true]]}
          layout={{ visibility }}
          paint={revealPaint(
            "line",
            { "line-color": ["get", "color"], "line-width": STROKE_WIDTH, "line-dasharray": [2.5, 1.5] },
            revealed,
          )}
        />
        {/* Préhension : un contour ou une ligne se saisit sans viser au pixel près. */}
        <Layer
          id={GRAPHIC_EDGE_LAYER_ID}
          type="line"
          filter={["in", ["get", "role"], STROKE_ROLES]}
          layout={{ visibility }}
          paint={revealPaint("line", { "line-color": "#000000", "line-width": 14, "line-opacity": 0.01 }, revealed)}
        />
      </Source>
      {visible &&
        tags.map((tag) => (
          <Marker key={tag.id} longitude={tag.at[0]} latitude={tag.at[1]} anchor="center" style={{ pointerEvents: "none" }}>
            {/* Fondu sur le contenu : le moteur réécrit l'opacité de l'élément du repère (occultation). */}
            <div style={{ ...revealStyle(revealed), transform: `translateY(${offsets.get(tag.id) ?? 0}px)` }}>
              <GraphicTag label={tag.label} color={tag.color} selected={tag.id === selectedId} />
            </div>
          </Marker>
        ))}
    </>
  );
}

/** Aperçu du tracé en cours : points posés et segment jusqu'au pointeur (ligne), ou empreinte de la zone et du
 *  panache sous le pointeur, avec les valeurs par défaut du type. */
function draftFeatures(draft: Draft | null): FeatureCollection {
  if (!draft) return EMPTY;
  const features: Feature[] = [];
  const props = { color: draft.color };
  const point = (at: LonLat) =>
    features.push({ type: "Feature", geometry: { type: "Point", coordinates: at }, properties: props });
  if (draft.purpose === "terrain") {
    // Zone d'analyse : polygone refermé dès trois sommets (sommet sous le pointeur compris).
    const ring = draft.hover ? [...draft.points, draft.hover] : draft.points;
    if (ring.length >= 3) {
      features.push({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [[...ring, ring[0]!]] },
        properties: props,
      });
    } else if (ring.length === 2) {
      features.push({ type: "Feature", geometry: { type: "LineString", coordinates: ring }, properties: { ...props, role: "hover" } });
    }
    draft.points.forEach(point);
    return { type: "FeatureCollection", features };
  }
  if (draft.family === "lineGraphics") {
    if (draft.points.length >= 2) {
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: draft.points },
        properties: { ...props, role: "line" },
      });
    }
    const last = draft.points[draft.points.length - 1];
    if (last && draft.hover) {
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [last, draft.hover] },
        properties: { ...props, role: "hover" },
      });
    }
    draft.points.forEach(point);
    return { type: "FeatureCollection", features };
  }
  if (!draft.hover) return EMPTY;
  const ring = (coords: LonLat[]) =>
    features.push({ type: "Feature", geometry: { type: "Polygon", coordinates: [coords] }, properties: props });
  if (draft.radius_m) ring(circle(draft.hover, draft.radius_m, draft.family === "cbrnPlumes" ? 48 : 64));
  if (draft.family === "cbrnPlumes" && draft.downwind_m) ring(hazardFan(draft.hover, 90, draft.downwind_m));
  point(draft.hover);
  return { type: "FeatureCollection", features };
}

export function DraftLayers({ Source, Layer }: Pick<EngineParts, "Source" | "Layer">) {
  const draft = useUi((s) => s.draft);
  const data = useMemo(() => draftFeatures(draft), [draft]);
  return (
    <Source id="gx-graphics-draft" type="geojson" data={data}>
      <Layer
        id="graphics-draft-fill"
        type="fill"
        filter={["==", ["geometry-type"], "Polygon"]}
        paint={{ "fill-color": ["get", "color"], "fill-opacity": 0.12 }}
      />
      <Layer
        id="graphics-draft-outline"
        type="line"
        filter={["==", ["geometry-type"], "Polygon"]}
        paint={{ "line-color": ["get", "color"], "line-width": 2, "line-dasharray": [2, 1.5] }}
      />
      <Layer
        id="graphics-draft-line"
        type="line"
        filter={["==", ["get", "role"], "line"]}
        layout={{ "line-cap": "round", "line-join": "round" }}
        paint={{ "line-color": ["get", "color"], "line-width": 3 }}
      />
      <Layer
        id="graphics-draft-hover"
        type="line"
        filter={["==", ["get", "role"], "hover"]}
        paint={{ "line-color": ["get", "color"], "line-width": 2, "line-dasharray": [2, 1.5], "line-opacity": 0.85 }}
      />
      <Layer
        id="graphics-draft-vertex"
        type="circle"
        filter={["==", ["geometry-type"], "Point"]}
        paint={{
          "circle-radius": 4,
          "circle-color": "#ffffff",
          "circle-stroke-color": ["get", "color"],
          "circle-stroke-width": 2,
        }}
      />
    </Source>
  );
}
